import type { FeedDay } from '@fruuit/shared'
import { formatDay } from './format'

/** Segment order (bottom → top) and colours; text always accompanies colour (STAT-008). */
const SEGMENTS = [
  { key: 'ok', label: 'Succeeded', color: '#7fa356' },
  { key: 'empty', label: 'No new records', color: '#b8d1cd' },
  { key: 'partial', label: 'Partial', color: '#d9b45a' },
  { key: 'late', label: 'Late', color: '#e98a4c' },
  { key: 'failed', label: 'Failed', color: '#9b3a24' },
] as const satisfies readonly { key: keyof Omit<FeedDay, 'day'>; label: string; color: string }[]

export function dayTotal(day: FeedDay): number {
  return day.ok + day.empty + day.partial + day.late + day.failed
}

function describeDay(day: FeedDay): string {
  const parts = SEGMENTS.filter((s) => day[s.key] > 0).map((s) => `${day[s.key]} ${s.label}`)
  return `${formatDay(day.day)}: ${parts.length ? parts.join(', ') : 'no runs'}`
}

export interface RunSparklineProps {
  days: readonly FeedDay[]
  /** Accessible name prefix ("Fruuit Screening Engine"). */
  label: string
  height?: number
  className?: string
}

/**
 * Seven-day run timeline as stacked bars: one bar per day, height proportional to the
 * number of runs, coloured by outcome. A tiny inline SVG, no chart library.
 */
export function RunSparkline({ days, label, height = 44, className }: RunSparklineProps) {
  const barWidth = 14
  const gap = 6
  const width = Math.max(1, days.length) * (barWidth + gap) - gap
  const max = Math.max(1, ...days.map(dayTotal))
  const problems = days.reduce((n, d) => n + d.failed + d.late + d.partial, 0)
  const total = days.reduce((n, d) => n + dayTotal(d), 0)
  const summary = `${label}: ${total} runs in the last ${days.length} days, ${problems} with problems.`

  return (
    <svg
      role="img"
      aria-label={summary}
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      className={className}
    >
      <title>{summary}</title>
      {days.map((day, index) => {
        const x = index * (barWidth + gap)
        const totalRuns = dayTotal(day)
        let y = height
        return (
          <g key={day.day}>
            <title>{describeDay(day)}</title>
            <rect x={x} y={0} width={barWidth} height={height} rx={2} fill="#f0f4ef" />
            {totalRuns === 0 ? (
              <rect x={x} y={height - 2} width={barWidth} height={2} fill="#c3cfc9" />
            ) : (
              SEGMENTS.map((segment) => {
                const count = day[segment.key]
                if (count === 0) return null
                const h = Math.max(2, (count / max) * height)
                y -= h
                return (
                  <rect
                    key={segment.key}
                    data-segment={segment.key}
                    x={x}
                    y={Math.max(0, y)}
                    width={barWidth}
                    height={h}
                    fill={segment.color}
                  />
                )
              })
            )}
          </g>
        )
      })}
    </svg>
  )
}

/** Legend for the sparkline colours. */
export function RunLegend({ className }: { className?: string }) {
  return (
    <ul className={`flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-sage ${className ?? ''}`}>
      {SEGMENTS.map((segment) => (
        <li key={segment.key} className="inline-flex items-center gap-1.5">
          <span
            aria-hidden="true"
            className="size-2 rounded-[2px]"
            style={{ backgroundColor: segment.color }}
          />
          {segment.label}
        </li>
      ))}
    </ul>
  )
}
